/** leads/AtvTable.tsx — 올댓벤처스 리스트와 «같은 규칙»의 표 (2026-09-10, 사용자 지시: 칼럼·좌우 열 움직임 동일)
 *
 *  ATV admin/js/views/list.js makeColumnsResizable 를 React로 옮긴 것. 규칙은 그대로다.
 *   ① table-layout:fixed + 열마다 px 폭 → 어느 열이든 내용 길이와 무관하게 좁힐 수 있다 (엑셀처럼)
 *   ② 표 좌우 끝선은 움직이지 않는다 — 어떤 열을 넓히면 그만큼 «오른쪽 열들»이 줄어들며 자리를 내주고, 좁히면 되돌려 받는다.
 *      마지막(리사이즈 대상) 열은 왼쪽 열들과 주고받는다. 오른쪽 열이 모두 최소폭(54px)이면 더 넓어지지 않는다.
 *   ③ 「흡수 열」(absorb, 주소·업종 같은 긴 텍스트 열)이 남는 폭을 먹어 오른쪽에 빈 공간이 남지 않는다. 창이 바뀌면 흡수 열만 다시 계산.
 *   ④ 왼쪽 고정 열(체크박스·No)과 오른쪽 고정 열(액션)은 폭 관리 대상에서 뺀다 (data-fixed).
 *   ⑤ 경계선 더블클릭 = 그 열 기본 폭 / Shift+더블클릭 = 표 전체 초기화. 폭은 localStorage `atv_colw_<key>` 에 저장(ATV와 같은 키 규칙).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

export interface AtvCol<T> {
  key: string;
  label: string;
  w: number;                  // 기본 폭(px)
  cls?: string;               // 'num' = 우측 정렬 · 'muted'
  absorb?: boolean;           // 흡수 열 (표당 1개)
  title?: string;             // 헤더 툴팁
  render: (row: T, idx: number) => React.ReactNode;
}
export interface AtvFixedCol<T> {
  key: string;
  fw: number;                 // 고정 폭
  header: React.ReactNode;
  cls?: string;
  render: (row: T, idx: number) => React.ReactNode;
  stopClick?: boolean;        // 셀 클릭이 행 클릭으로 번지지 않게
}
interface Props<T> {
  storeKey: string;                     // localStorage 키 접미사 (ATV: refund / pool/venture / pool/capital)
  rows: T[];
  rowKey: (row: T) => string;
  lead?: AtvFixedCol<T>[];              // 왼쪽 고정 열 (체크박스 · No)
  name: { label: string; w: number; render: (row: T, idx: number) => React.ReactNode }; // 회사명 (sticky-l)
  cols: AtvCol<T>[];
  actions?: AtvFixedCol<T>;             // 오른쪽 고정 열 (sticky-r)
  onRowClick?: (row: T) => void;
  rowClass?: (row: T) => string;
  empty?: React.ReactNode;
}

const MIN = 54;
const LEAD_DEFAULT = 38;

/** 저장 폭 = 열 이름(id)별 (2026-10-27 — 자리 번호로 저장하면 열이 늘거나 옮겨질 때 옛 폭이 엉뚱한 열에 붙는다 · ATV 신규법인 리스트 사고)
 *  옛 자리 번호 저장은 열 수가 같을 때만 이름으로 옮기고, 다르면 버린다 */
function loadSaved(key: string, ids: string[]): Record<string, number> {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(key) || '{}');
    if (!raw || typeof raw !== 'object') return {};
    const o = raw as Record<string, unknown>;
    if (o.v === 2 && o.w && typeof o.w === 'object') return o.w as Record<string, number>;
    const nums = Object.keys(o).filter((k) => /^\d+$/.test(k));
    const out: Record<string, number> = {};
    if (nums.length === ids.length) nums.forEach((k) => { const v = Number(o[k]); if (v > 0) out[ids[Number(k)]] = v; });
    return out;
  } catch { return {}; }
}

export default function AtvTable<T>({ storeKey, rows, rowKey, lead, name, cols, actions, onRowClick, rowClass, empty }: Props<T>) {
  const KEY = 'atv_colw_' + storeKey;
  const scrollRef = useRef<HTMLDivElement>(null);
  // 리사이즈 대상 열 = 회사명 + cols (고정 열 제외). 인덱스 0 = 회사명
  // 열 구성 서명(문자열) — 부모가 렌더마다 cols 배열을 새로 만들어도 구성이 같으면 폭을 초기화하지 않는다
  const sig = [Math.max(MIN, name.w), ...cols.map((c) => Math.max(MIN, c.w || 90))].join(',');
  const defaults = useMemo(() => sig.split(',').map(Number), [sig]);
  const idSig = ['__name', ...cols.map((c) => c.key)].join('\u0001');
  const ids = useMemo(() => idSig.split('\u0001'), [idSig]);
  const absorbSig = cols.findIndex((c) => c.absorb);
  const absorbIdx = useMemo(() => (absorbSig < 0 ? -1 : absorbSig + 1), [absorbSig]);
  const fixedLead = (lead || []).reduce((a, c) => a + (c.fw || LEAD_DEFAULT), 0);
  const fixedRight = actions ? actions.fw : 0;
  const [widths, setWidths] = useState<number[]>(() => {
    const saved = loadSaved(KEY, ids);
    return defaults.map((d, i) => Math.max(MIN, Math.round(saved[ids[i]] || d)));
  });
  const widthsRef = useRef(widths); widthsRef.current = widths;
  const [dragging, setDragging] = useState<number>(-1);

  /** 표 폭을 컨테이너에 양방향으로 맞춘다 — 남으면 흡수 열이 먹고, 넘치면 흡수 열부터 되돌려 받는다 (ATV fitToBox) */
  const fitToBox = useCallback((base: number[]): number[] => {
    if (absorbIdx < 0) return base;
    const box = scrollRef.current;
    const avail = box ? box.clientWidth : 0;
    if (!avail) return base;
    const sum = base.reduce((a, b) => a + b, 0);
    const slack = avail - (fixedLead + fixedRight + sum);
    if (slack === 0) return base;
    const next = base.slice();
    if (slack > 0) { next[absorbIdx] += slack; return next; }
    const def = defaults[absorbIdx];
    const floor = Math.min(base[absorbIdx], def);
    next[absorbIdx] = Math.max(floor, base[absorbIdx] + slack);
    return next;
  }, [absorbIdx, defaults, fixedLead, fixedRight]);

  // 열 구성이 바뀌면(다른 화면) 저장값/기본값으로 재초기화
  useEffect(() => {
    const saved = loadSaved(KEY, ids);
    setWidths(defaults.map((d, i) => Math.max(MIN, Math.round(saved[ids[i]] || d))));
  }, [KEY, defaults, ids]);

  // 마운트·리사이즈 시 흡수 열 재계산
  useLayoutEffect(() => {
    setWidths((w) => { const n = fitToBox(w); return n.some((v, i) => v !== w[i]) ? n : w; });
    const onResize = () => setWidths((w) => { const n = fitToBox(w); return n.some((v, i) => v !== w[i]) ? n : w; });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fitToBox, rows.length]);

  const persist = (w: number[]) => {
    const saved: Record<string, number> = {};
    w.forEach((v, i) => { saved[ids[i]] = v; });
    try { localStorage.setItem(KEY, JSON.stringify({ v: 2, w: saved })); } catch { /* 무시 */ }
  };

  /** idx 열을 targetW 로 만들되 표 전체 폭은 그대로 — 오른쪽 열들(마지막이면 왼쪽 열들)과 주고받는다 (ATV resizeTo) */
  const resizeTo = (idx: number, targetW: number, snap: number[], partners: number[]): number[] => {
    const total = snap.reduce((a, b) => a + b, 0);
    const capacity = partners.reduce((a, j) => a + (snap[j] - MIN), 0);
    const d = Math.max(MIN - snap[idx], Math.min(targetW - snap[idx], capacity));
    const next = snap.slice();
    if (d > 0 && capacity > 0) {
      let given = 0;
      partners.forEach((j, k) => {
        const cap = snap[j] - MIN;
        let take = (k === partners.length - 1) ? d - given : Math.round(d * (cap / capacity));
        take = Math.max(0, Math.min(take, cap));
        next[j] = snap[j] - take; given += take;
      });
    } else if (d < 0) {
      const back = -d;
      const tot = partners.reduce((a, j) => a + snap[j], 0);
      let given = 0;
      partners.forEach((j, k) => {
        const add = (k === partners.length - 1) ? back - given : Math.round(tot > 0 ? back * (snap[j] / tot) : 0);
        next[j] = snap[j] + add; given += add;
      });
    }
    const others = new Set(partners);
    let rest = 0;
    for (let j = 0; j < next.length; j++) if (j !== idx && !others.has(j)) rest += next[j];
    let mine = total - rest - partners.reduce((a, j) => a + next[j], 0);
    if (mine < MIN) mine = MIN;
    next[idx] = mine;
    return next;
  };
  const partnersOf = (idx: number, n: number): number[] => {
    const right: number[] = []; for (let j = idx + 1; j < n; j++) right.push(j);
    if (right.length) return right;
    const left: number[] = []; for (let j = idx - 1; j >= 0; j--) left.push(j);
    return left;
  };

  const onGripDown = (idx: number) => (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const snap = widthsRef.current.slice();
    const partners = partnersOf(idx, snap.length);
    setDragging(idx);
    const move = (ev: MouseEvent) => setWidths(resizeTo(idx, snap[idx] + (ev.clientX - startX), snap, partners));
    const up = () => {
      document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up);
      setDragging(-1); persist(widthsRef.current);
    };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
  };
  const onGripDbl = (idx: number) => (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (e.shiftKey) {
      try { localStorage.removeItem(KEY); } catch { /* 무시 */ }
      setWidths(fitToBox(defaults.slice()));
      return;
    }
    const next = resizeTo(idx, defaults[idx], widthsRef.current.slice(), partnersOf(idx, widthsRef.current.length));
    setWidths(next); persist(next);
  };

  const tableW = fixedLead + fixedRight + widths.reduce((a, b) => a + b, 0);
  const leadCols = lead || [];
  let leftAcc = 0;
  const leadLefts = leadCols.map((c) => { const l = leftAcc; leftAcc += c.fw || LEAD_DEFAULT; return l; });
  const nameLeft = leftAcc;

  const grip = (idx: number, last: boolean) => (last ? null : (
    <span className={'atv-grip' + (dragging === idx ? ' on' : '')} onMouseDown={onGripDown(idx)} onDoubleClick={onGripDbl(idx)} title="끌어서 폭 조절 · 더블클릭 기본 폭 · Shift+더블클릭 전체 초기화" />
  ));

  return (
    <div className="atv-tblcard">
      <div className="atv-scroll" ref={scrollRef}>
        <table className="atv-list" style={{ width: tableW, tableLayout: 'fixed', minWidth: 0 }}>
          <thead>
            <tr>
              {leadCols.map((c, i) => (
                <th key={c.key} className={'atv-fixed atv-sticky-l ' + (c.cls || '')} style={{ width: c.fw, minWidth: 0, maxWidth: 'none', left: leadLefts[i] }}>{c.header}</th>
              ))}
              <th className="atv-sticky-l atv-name" style={{ width: widths[0], minWidth: 0, maxWidth: 'none', left: nameLeft }}>{name.label}{grip(0, cols.length === 0)}</th>
              {cols.map((c, i) => (
                <th key={c.key} className={c.cls || ''} title={c.title} style={{ width: widths[i + 1], minWidth: 0, maxWidth: 'none' }}>
                  {c.label}{grip(i + 1, i === cols.length - 1)}
                </th>
              ))}
              {actions && <th className={'atv-fixed atv-sticky-r ' + (actions.cls || '')} style={{ width: actions.fw, minWidth: 0, maxWidth: 'none' }}>{actions.header}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={rowKey(r)} className={rowClass ? rowClass(r) : ''} onClick={onRowClick ? () => onRowClick(r) : undefined} style={onRowClick ? undefined : { cursor: 'default' }}>
                {leadCols.map((c, i) => (
                  <td key={c.key} className={'atv-fixed atv-sticky-l ' + (c.cls || '')} style={{ left: leadLefts[i] }} onClick={c.stopClick ? (e) => e.stopPropagation() : undefined}>{c.render(r, idx)}</td>
                ))}
                <td className="atv-sticky-l atv-name" style={{ left: nameLeft }}>{name.render(r, idx)}</td>
                {cols.map((c) => <td key={c.key} className={c.cls || ''}>{c.render(r, idx)}</td>)}
                {actions && <td className={'atv-fixed atv-sticky-r ' + (actions.cls || '')} onClick={actions.stopClick ? (e) => e.stopPropagation() : undefined}>{actions.render(r, idx)}</td>}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td className="atv-empty" colSpan={leadCols.length + 1 + cols.length + (actions ? 1 : 0)}>{empty || '표시할 데이터가 없습니다'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** 페이지네이션 (ATV tbl-foot: 「총 N건 중 a–b 표시」 + ‹ 1 … n ›) */
export function AtvPager({ total, page, pageSize, onPage, left }: { total: number; page: number; pageSize: number; onPage: (p: number) => void; left?: React.ReactNode }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const start = total ? (page - 1) * pageSize + 1 : 0;
  const end = Math.min(total, page * pageSize);
  const items: (number | '…')[] = [];
  let last = 0;
  for (let p = 1; p <= pages; p++) {
    if (p === 1 || p === pages || Math.abs(p - page) <= 2) { if (last && p - last > 1) items.push('…'); items.push(p); last = p; }
  }
  return (
    <div className="atv-foot">
      {left}
      <span>총 {total.toLocaleString()}건 중 {start.toLocaleString()}–{end.toLocaleString()} 표시</span>
      <div className="atv-pg">
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
        {items.map((it, i) => (it === '…' ? <button key={'e' + i} type="button" disabled>…</button>
          : <button key={it} type="button" className={it === page ? 'on' : ''} onClick={() => onPage(it)}>{it}</button>))}
        <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}>›</button>
      </div>
    </div>
  );
}

/* ── 셀 헬퍼 (ATV ui.js fmtMoney / finCell / deadlineCls 동일 의미) ── */
export function fmtMoney(v: number | null | undefined): string {
  if (v == null || isNaN(Number(v))) return '—';
  const n = Number(v);
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e8) { const x = a / 1e8; return sign + (x % 1 === 0 ? x : Number(x.toFixed(1))) + '억'; }
  if (a >= 1e4) return sign + Math.round(a / 1e4).toLocaleString() + '만';
  return sign + a.toLocaleString() + '원';
}
export function FinCell({ v, fsYear }: { v: number | null | undefined; fsYear?: number | null }) {
  const money = fmtMoney(v);
  if (money === '—' || fsYear == null) return <>{money}</>;
  return <span><span style={{ color: Number(v) < 0 ? '#DC2626' : undefined }}>{money}</span><span className="atv-finy">({String(fsYear).slice(2)}년)</span></span>;
}
export function deadlineCls(deadline?: string | null): string {
  if (!deadline) return '';
  const d = Math.floor((new Date(deadline + 'T23:59:59').getTime() - Date.now()) / 86400000);
  if (d <= 365) return 'urgent';
  if (d <= 730) return 'warn';
  return '';
}
